import { PlayTogetherName } from '@league/ui';
import { DurationChoices } from '../../components/PlayingPreferences';
import { type PlayingPace, type PlayTogetherDuration, PACE_OPTIONS } from '@league/shared/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useId, useState } from 'react';
import { CalendarDays, Trophy, UsersRound, Settings } from 'lucide-react';
import { PlayerPage } from './PlayerPage';
import './profile.css';
import { Link, useOutletContext } from 'react-router-dom';

import type { Actor } from '../../lib/auth';

import type { PlayerCareer, PlayerRating } from '@league/shared/contract';
import { PUBLIC_STANDINGS_IN_V1 } from '@league/shared/features';
import { RATING_ESTABLISHED_AT } from '@league/shared/scoring';

import { Button, ErrorNote } from '../../components/ui';
import { ApiError, apiFetch, apiPost } from '../../lib/api';

/**
 * YOU — her record, how she likes to play, and her account.
 *
 * Three blocks in that order because that is their order of interest. Nobody opens this tab to
 * change a setting; they open it to see what they have done, and the settings are underneath
 * because they have to live somewhere and a fourth tab is not allowed.
 *
 * TWO THINGS SHE CANNOT SET, and both say so on the screen rather than being quietly absent:
 * her experience level, which remains separate from preferred playing pace, and
 * her sit-with requests once the roster has locked and the print pack has been made.
 */

interface Profile {
  playerId: string;
  firstName: string;
  lastName: string;
  email: string;
  skillLevel: 'beginner' | 'intermediate' | 'advanced';
  selfReportedSkill: string | null;
  preferredPace: PlayingPace | null;
  nmjlCard: boolean;
  surnamePublic: boolean;
  emailUnsubscribed: boolean;
  twoStepSignIn: boolean;
}

interface Record_ {
  leaguesPlayed: number;
  sessionsWon: number;
  bestFinish: number | null;
  handsWon: number;
  playedWith: { met: number; outOf: number } | null;
  career: PlayerCareer;
  rating: PlayerRating;
}

interface League {
  playTogetherEligible: boolean;
  leagueId: string;
  name: string;
  status: string;
  city: string;
  state: string;
}

interface SitWith {
  selectedPlayerId: string | null;
  id: string;
  leagueId: string;
  leagueName: string;
  requestedName: string;
  matched: boolean;
  duration: PlayTogetherDuration;
  editable: boolean;
}

interface Payload {
  profile: Profile | null;
  record: Record_ | null;
  leagues: League[];
  sitWith: SitWith[];
}

export function YouPage({ onSignOut }: { onSignOut: () => void }) {
  /* The shell passes the signed-in actor down. Staff here are directors who also play. */
  const staff = useOutletContext<Actor | undefined>()?.role !== 'player';
  const queryClient = useQueryClient();
  const [duration, setDuration] = useState<PlayTogetherDuration>('first_session');
  const [asking, setAsking] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [editingRequest, setEditingRequest] = useState<string | undefined>();

  const meta = useQuery({
    queryKey: ['meta'],
    queryFn: () => apiFetch<{ brand: string }>('/api/meta'),
    staleTime: Infinity,
  });
  const brand = meta.data?.brand ?? 'League';

  const data = useQuery({
    queryKey: ['my-you'],
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    queryFn: () => apiFetch<Payload>('/api/my'),
  });

  const available = (data.data?.leagues ?? []).filter((league) => league.playTogetherEligible);
  const selectedLeague = asking ?? available[0]?.leagueId ?? null;
  const lookup = useCallback(
    async (query: string) =>
      (
        await apiFetch<{ suggestions: { name: string; playerId: string | null }[] }>(
          `/api/my/sit-with/suggestions?leagueId=${encodeURIComponent(selectedLeague ?? '')}&q=${encodeURIComponent(query)}`,
        )
      ).suggestions,
    [selectedLeague],
  );

  const patch = useMutation({
    mutationFn: (body: Partial<Profile>) =>
      apiFetch<{ profile: Profile }>('/api/my/profile', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-you'] }),
  });

  const ask = useMutation({
    mutationFn: (body: {
      leagueId: string;
      name: string;
      duration: PlayTogetherDuration;
      selectedPlayerId: string | null;
      requestId?: string;
    }) => apiPost<{ sitWith: SitWith[] }>('/api/my/sit-with', body),
    onSuccess: async () => {
      setAsking(null);
      setName('');
      setSelectedPlayerId(null);
      setEditingRequest(undefined);
      await queryClient.invalidateQueries({ queryKey: ['my-you'] });
    },
  });

  const withdraw = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ sitWith: SitWith[] }>(`/api/my/sit-with/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-you'] }),
  });

  if (data.isError)
    return (
      <Shell>
        <ErrorNote>
          Could not load your profile.{' '}
          <Button variant="secondary" onClick={() => void data.refetch()}>
            Try again
          </Button>
        </ErrorNote>
      </Shell>
    );
  if (data.isPending) return <p className="px-6 py-10 text-sm text-ink-3">Loading…</p>;

  const profile = data.data?.profile;
  const record = data.data?.record;
  const leagues = data.data?.leagues ?? [];
  const sitWith = data.data?.sitWith ?? [];

  const error =
    (ask.error ?? withdraw.error) instanceof ApiError
      ? ((ask.error ?? withdraw.error) as ApiError).message
      : null;

  if (!profile) {
    return (
      <Shell>
        <p className="text-[15px] leading-relaxed text-ink-2">
          This account is not linked to a player record yet. Your director can sort that out.
        </p>
        <Button variant="secondary" onClick={onSignOut}>
          Sign out
        </Button>
      </Shell>
    );
  }

  const open = available;
  const requestLeagues = [
    ...open,
    ...leagues.filter(
      (league) =>
        league.leagueId === selectedLeague &&
        !open.some((item) => item.leagueId === league.leagueId),
    ),
  ];

  return (
    <Shell>
      <header className="profile-welcome">
        <div>
          <h1>Hi, {profile.firstName || 'there'}.</h1>
          <p>A little friendly competition. A lot to look forward to.</p>
        </div>
      </header>

      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {patch.isError ? (
        <ErrorNote>
          {patch.error instanceof ApiError
            ? patch.error.message
            : 'Could not save your preference. Please try again.'}
        </ErrorNote>
      ) : null}
      {patch.isSuccess ? (
        <p role="status" className="text-sm text-sage">
          Preference saved.
        </p>
      ) : null}

      {record ? (
        <>
          <RatingPanel rating={record.rating} brand={brand} />
          <div className="profile-stats">
            <Stat
              label="Leagues played"
              value={record.career.leaguesPlayed}
              icon={<UsersRound />}
            />
            <Stat
              label="Sessions played"
              value={record.career.sessionsPlayed}
              icon={<CalendarDays />}
            />
            <Stat
              label="Hands won"
              value={record.career.handsWonVerified ? record.career.handsWon : null}
              icon={<Trophy />}
            />
          </div>
          <RatingJourney rating={record.rating} />
        </>
      ) : (
        <p className="profile-card">Your playing record will appear here as you play.</p>
      )}

      <section className="profile-pace profile-card">
        <h2>How you like to play</h2>
        <p className="text-ink-2">Choose the pace that feels right for you.</p>
        <ProfilePace
          value={profile.preferredPace}
          disabled={patch.isPending}
          onChange={(preferredPace) => patch.mutate({ preferredPace })}
        />
      </section>

      <section className="profile-card profile-leagues">
        <h2>Your leagues</h2>
        <ul className="mt-2 flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
          {leagues.map((league) => (
            <li
              key={league.leagueId}
              className="flex items-baseline justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0">
                <p className="break-words font-display text-[22px]">{league.name}</p>
                <p className="text-[12px] text-ink-3">
                  {league.city}, {league.state}
                </p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  <Link
                    className="inline-flex min-h-[44px] items-center underline focus-visible:outline-2"
                    to={`/my?leagueId=${encodeURIComponent(league.leagueId)}`}
                  >
                    Session details
                  </Link>
                  <Link
                    className="inline-flex min-h-[44px] items-center underline focus-visible:outline-2"
                    to={`/my/standings?leagueId=${encodeURIComponent(league.leagueId)}`}
                  >
                    Standings
                  </Link>
                </div>
              </div>
              {league.status === 'active' ? (
                <span className="shrink-0 rounded-full bg-tint-signal px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-on-tint-signal">
                  In play
                </span>
              ) : (
                <span className="shrink-0 text-[12px] text-ink-3">
                  {league.status === 'completed'
                    ? 'Finished'
                    : league.status === 'draft'
                      ? 'In preparation'
                      : league.status === 'registration_open'
                        ? 'Registration open'
                        : league.status.replaceAll('_', ' ')}
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>

      <details className="profile-card profile-account">
        <summary>
          <Settings aria-hidden="true" size={26} />
          <span>
            Account settings<small>Email preferences, password, and more.</small>
          </span>
        </summary>
        <div className="mt-5 space-y-3">
          <p className="text-sm text-ink-2 break-words">
            {profile.firstName} {profile.lastName} · {profile.email}
          </p>
          <div className="mt-2 flex flex-col gap-3">
            <div className="rounded-md border border-light bg-paper px-4 py-4">
              <p className="text-[14px] font-medium">Play Together</p>
              <p className="mt-1 text-[13px] leading-relaxed text-ink-2">
                {open.length
                  ? 'Your Director will consider your request.'
                  : 'Play Together requests aren’t available for your leagues right now.'}
              </p>

              {sitWith.length > 0 ? (
                <ul className="mt-3 flex flex-col gap-2">
                  {sitWith.map((request) => (
                    <li
                      key={request.id}
                      className="flex items-baseline justify-between gap-3 rounded-card bg-sand px-3.5 py-2.5"
                    >
                      <div className="min-w-0">
                        <p className="text-[15px]">
                          {request.requestedName} ·{' '}
                          {request.duration === 'first_session'
                            ? 'First league night'
                            : 'Every league night'}
                        </p>
                        <p className="text-[12px] text-ink-3">
                          {request.leagueName}
                          {request.matched ? ' · found' : ' · your director is matching this up'}
                        </p>
                      </div>
                      {request.editable &&
                      open.some((league) => league.leagueId === request.leagueId) ? (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="secondary"
                            onClick={() => {
                              setAsking(request.leagueId);
                              setName(request.requestedName);
                              setDuration(request.duration);
                              setSelectedPlayerId(request.selectedPlayerId);
                              setEditingRequest(request.id);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="destructive"

                            onClick={() => withdraw.mutate(request.id)}
                            className="shrink-0"
                          >
                            Remove
                          </Button>
                        </div>
                      ) : (
                        /*
                        Said out loud rather than the button quietly vanishing. After the lock the
                        table cards have been printed from that roster, so a change here would not
                        reach the room — but her director still can move people.
                      */
                        <span className="shrink-0 text-[12px] text-ink-3">Locked in</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}

              {open.length > 1 && !editingRequest ? (
                <label className="mt-3 block text-[14px]">
                  League
                  <select
                    aria-label="League"
                    className="mt-1 min-h-[44px] w-full rounded-md border border-stroke bg-paper px-3"
                    value={selectedLeague ?? ''}
                    onChange={(event) => {
                      setAsking(event.target.value);
                      setName('');
                      setSelectedPlayerId(null);
                      setDuration('first_session');
                      ask.reset();
                    }}
                  >
                    {open.map((league) => (
                      <option key={league.leagueId} value={league.leagueId}>
                        {league.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {requestLeagues
                .filter(
                  (league) =>
                    league.leagueId === selectedLeague &&
                    (league.playTogetherEligible || editingRequest),
                )
                .map((league) => (
                  <div key={league.leagueId} className="mt-3 flex flex-wrap items-center gap-2">
                    {open.length <= 1 || editingRequest ? (
                      <p className="text-[13px] text-ink-2">{league.name}</p>
                    ) : null}
                    <PlayTogetherName
                      leagueKey={league.leagueId}
                      value={name}
                      selectedPlayerId={selectedPlayerId}
                      placeholder="Start typing your friend’s name"
                      helper="If they haven’t registered yet, you can still leave a request."
                      onChange={(text, id) => {
                        setName(text);
                        setSelectedPlayerId(id);
                        ask.reset();
                      }}
                      lookup={lookup}
                    />
                    <div className="w-full">
                      <DurationChoices
                        leagueNights
                        value={duration}
                        onChange={setDuration}
                        disabled={ask.isPending}
                      />
                    </div>
                    <Button
                      disabled={ask.isPending || name.trim() === ''}
                      onClick={() =>
                        ask.mutate({
                          leagueId: league.leagueId,
                          name,
                          duration,
                          selectedPlayerId,
                          ...(editingRequest ? { requestId: editingRequest } : {}),
                        })
                      }
                    >
                      {editingRequest ? 'Save request' : 'Ask'}
                    </Button>
                    {editingRequest ? (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setEditingRequest(undefined);
                          setName('');
                          setSelectedPlayerId(null);
                          setDuration('first_session');
                          setAsking(null);
                        }}
                      >
                        Cancel
                      </Button>
                    ) : null}
                  </div>
                ))}
              {ask.isSuccess ? (
                <p role="status" className="mt-2 text-sm text-sage">
                  Request saved.
                </p>
              ) : null}
            </div>

            {/*
            ONLY WHILE THERE IS A PUBLIC PAGE. The switch governed the public standings page and its
            image; with that page switched off (owner, 2026-09-26) it would govern nothing, and a
            switch that does nothing tells her something false. It returns with the page.
          */}
            {PUBLIC_STANDINGS_IN_V1 ? (
              <Toggle
                label="Show my surname publicly"
                hint="Standings pages your director shares are public. Turn this off and you appear as your first name and an initial, in the page and in any image saved from it."
                on={profile.surnamePublic}
                onChange={(surnamePublic) => patch.mutate({ surnamePublic })}
              />
            ) : null}
          </div>
          <div className="mt-2 flex flex-col gap-3">
            {/*
            ONE SWITCH, AND IT SAYS WHAT IT DOES NOT COVER. The unsubscribe page has pointed players
            here since the public site launched; until 2026-09-26 there was nothing here to find.
          */}
            <Toggle
              label="Email from your league"
              hint="Receive reminders, results, and messages from your League Director. Account and security emails are sent separately."
              on={!profile.emailUnsubscribed}
              onChange={(on) => patch.mutate({ emailUnsubscribed: !on })}
            />

            {/*
            A DIRECTOR WHO PLAYS (owner, 2026-09-26) is on her director login, where two-step sign-in
            is required and the account holds her leagues. So those two controls are not offered to
            her here — the API refuses both for staff anyway — and the page says why.
          */}
            {staff ? (
              <p className="rounded-md border border-light bg-paper px-4 py-4 text-[13px] leading-relaxed text-ink-2">
                You play on your director login, so two-step sign-in stays on and your account is
                managed from your director settings.
              </p>
            ) : (
              <TwoStep
                on={profile.twoStepSignIn}
                onChanged={() => queryClient.invalidateQueries({ queryKey: ['my-you'] })}
              />
            )}

            <ChangePassword />

            <div className="rounded-md border border-light bg-paper px-4 py-4">
              <p className="text-[13px] leading-relaxed text-ink-2">
                Contact your League Director to update your name or email.
              </p>
              <Button variant="secondary" className="mt-4" onClick={onSignOut}>
                Sign out
              </Button>
            </div>

            {staff ? null : <DeleteAccount onDeleted={onSignOut} />}
          </div>
        </div>
      </details>
    </Shell>
  );
}

/** A password field sized for a thumb, with the browser's own password handling left intact. */
function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
}) {
  return (
    <label htmlFor={id} className="flex flex-col gap-1.5">
      <span className="text-[13px] font-medium">{label}</span>
      <input
        id={id}
        type="password"
        autoComplete={autoComplete}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 rounded-md border border-control-border bg-chalk px-3 text-[15px]"
      />
    </label>
  );
}

const MIN_PASSWORD = 10;

/**
 * CHANGING HER PASSWORD — better-auth's own endpoint, called directly. It checks the current one and
 * signs every OTHER device out, which is what somebody changing a password usually wants: the phone
 * she lost stops being signed in.
 */
function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [done, setDone] = useState(false);

  const change = useMutation({
    mutationFn: () =>
      apiPost('/api/auth/change-password', {
        currentPassword: current,
        newPassword: next,
        revokeOtherSessions: true,
      }),
    onSuccess: () => {
      setOpen(false);
      setDone(true);
      setCurrent('');
      setNext('');
      setAgain('');
    },
  });

  const mismatch = again.length > 0 && next !== again;
  const ready = current.length > 0 && next.length >= MIN_PASSWORD && next === again;

  return (
    <div className="rounded-md border border-light bg-paper px-4 py-4">
      <p className="text-[14px] font-medium">Password</p>
      {done ? (
        <p role="status" className="mt-1 text-[13px] text-ink-2">
          Changed. Any other phone or computer you were signed in on has been signed out.
        </p>
      ) : null}
      {open ? (
        <form
          className="mt-3 flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) change.mutate();
          }}
        >
          <PasswordField
            id="pw-current"
            label="Your current password"
            value={current}
            onChange={setCurrent}
            autoComplete="current-password"
          />
          <PasswordField
            id="pw-new"
            label={`A new one — at least ${MIN_PASSWORD} characters`}
            value={next}
            onChange={setNext}
            autoComplete="new-password"
          />
          <PasswordField
            id="pw-again"
            label="The new one again"
            value={again}
            onChange={setAgain}
            autoComplete="new-password"
          />
          {mismatch ? <p className="text-[13px] text-ink-2">Those two do not match yet.</p> : null}
          {change.error ? (
            <ErrorNote>
              {change.error instanceof ApiError
                ? change.error.message
                : 'Could not change it. Try again.'}
            </ErrorNote>
          ) : null}
          <div className="flex flex-wrap gap-3">
            <Button type="submit" disabled={!ready || change.isPending}>
              {change.isPending ? 'Changing…' : 'Change password'}
            </Button>
            <Button variant="secondary" type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button
          variant="secondary"

          className="mt-2"
          onClick={() => {
            setDone(false);
            setOpen(true);
          }}
        >
          Change your password
        </Button>
      )}
    </div>
  );
}

/**
 * TWO-STEP SIGN-IN — the six-digit code staff use, hers to switch on. It asks for her password
 * either way, because turning protection off should need the thing only she knows.
 */
function TwoStep({ on, onChanged }: { on: boolean; onChanged: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');

  const save = useMutation({
    mutationFn: () => apiPost('/api/my/account/two-step', { enabled: !on, password }),
    onSuccess: () => {
      setConfirming(false);
      setPassword('');
      onChanged();
    },
  });

  return (
    <div className="rounded-md border border-light bg-paper px-4 py-4">
      <p className="text-[14px] font-medium">Two-step sign-in {on ? 'is on' : 'is off'}</p>
      <p className="mt-1 text-[13px] leading-relaxed text-ink-2">
        {on
          ? 'When you sign in, we also email you a six-digit code.'
          : 'Turn this on and signing in also asks for a six-digit code we email you. It keeps your account yours if somebody learns your password.'}
      </p>
      {confirming ? (
        <form
          className="mt-3 flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (password) save.mutate();
          }}
        >
          <PasswordField
            id="twostep-pw"
            label="Your password, to confirm it is you"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
          />
          {save.error ? (
            <ErrorNote>
              {save.error instanceof ApiError
                ? save.error.message
                : 'Could not change it. Try again.'}
            </ErrorNote>
          ) : null}
          <div className="flex flex-wrap gap-3">
            <Button type="submit" disabled={!password || save.isPending}>
              {on ? 'Turn it off' : 'Turn it on'}
            </Button>
            <Button variant="secondary" type="button" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="secondary" className="mt-2" onClick={() => setConfirming(true)}>
          {on ? 'Turn two-step sign-in off' : 'Turn two-step sign-in on'}
        </Button>
      )}
    </div>
  );
}

/**
 * DELETE MY ACCOUNT. Ruled 2026-09-25: her login goes and she shows as "Former player"; her past
 * scores and the standings they made stay. The panel says exactly that before she presses anything,
 * because "delete" alone reads as "my scores disappear and everybody's standings change" — neither
 * of which happens.
 *
 * Quiet at rest: one small outlined button at the bottom, never a filled red button waiting on the screen.
 */
function DeleteAccount({ onDeleted }: { onDeleted: () => void }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');

  const remove = useMutation({
    mutationFn: () => apiPost('/api/my/account/delete', { password }),
    onSuccess: onDeleted,
  });

  if (!open) {
    return (
      <Button variant="destructive" className="self-start" onClick={() => setOpen(true)}>
        Delete my account
      </Button>
    );
  }

  return (
    <form
      className="rounded-md border border-light bg-paper px-4 py-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (password) remove.mutate();
      }}
    >
      <p className="text-[14px] font-medium">Delete your account?</p>
      <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-[13px] leading-relaxed text-ink-2">
        <li>
          You will not be able to sign in again, and your name, email and phone number are removed.
        </li>
        <li>
          The games you played stay in the leagues you played them in, shown as &ldquo;Former
          player&rdquo;, so nobody else&rsquo;s standings change.
        </li>
        <li>A league that has not started yet will no longer have you on it.</li>
      </ul>
      <div className="mt-3">
        <PasswordField
          id="delete-pw"
          label="Your password, to confirm"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
        />
      </div>
      {remove.error ? (
        <div className="mt-3">
          <ErrorNote>
            {remove.error instanceof ApiError
              ? remove.error.message
              : 'Could not delete it. Try again.'}
          </ErrorNote>
        </div>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-3">
        <Button variant="destructive" type="submit" disabled={!password || remove.isPending}>
          {remove.isPending ? 'Deleting…' : 'Delete my account'}
        </Button>
        <Button variant="secondary" type="button" onClick={() => setOpen(false)}>
          Keep my account
        </Button>
      </div>
    </form>
  );
}

/** Display only: the API remains the authority for eligibility, rating and history. */
export function RatingPanel({ rating, brand }: { rating: PlayerRating; brand: string }) {
  const headingId = useId();
  const value = rating.performancePercentile;
  const rated =
    rating.status !== 'unrated' &&
    value !== null &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 100;
  const provisional = rating.status === 'provisional';
  const interpretation = rated
    ? `Across your sessions, your average finish is at the ${value.toFixed(1)}th percentile.`
    : null;
  return (
    <section className="profile-rating" aria-labelledby={headingId}>
      <div className="profile-rating-header">
        <div>
          <h2 id={headingId}>Your {brand} rating</h2>
          <p className={`profile-rating-number tabular${rated ? '' : ' profile-rating-empty'}`}>
            {rated ? (
              <>
                {value.toFixed(1)} <span>/ 100</span>
              </>
            ) : (
              'Not yet rated'
            )}
          </p>
          <p className="profile-rating-subtitle">Your overall performance across sessions.</p>
        </div>
        {rating.status !== 'unrated' ? (
          <p className="profile-rating-status">
            {rating.status === 'established'
              ? `Established · ${rating.sessions} sessions`
              : `Provisional · ${rating.sessions} of ${RATING_ESTABLISHED_AT} sessions`}
          </p>
        ) : null}
      </div>
      <div
        className="profile-rating-gauge"
        role="img"
        aria-label={
          rated
            ? `${provisional ? 'Provisional rating' : 'Rating'}: ${value.toFixed(1)} out of 100. Scale from lower finishes at 0, through the middle of the field at 50, to higher finishes at 100.`
            : 'Rating scale from 0 to 100. Not yet rated.'
        }
      >
        <div aria-hidden="true">
          <div className="profile-rating-scale">
            {rated ? (
              <span
                className="profile-rating-marker-label tabular"
                style={{ left: `clamp(24px, ${value}%, calc(100% - 24px))` }}
              >
                {value.toFixed(1)}
              </span>
            ) : null}
            <div className="profile-rating-track">
              {rated ? (
                <span className="profile-rating-fill" style={{ width: `${value}%` }} />
              ) : null}
            </div>
            {rated ? (
              <span className="profile-rating-marker" style={{ left: `${value}%` }} />
            ) : null}
          </div>
          <div className="profile-rating-ticks tabular">
            {[0, 25, 50, 75, 100].map((tick) => (
              <span key={tick}>{tick}</span>
            ))}
          </div>
          <div className="profile-rating-references">
            <span>Lower finishes</span>
            <span>Middle of the field</span>
            <span>Higher finishes</span>
          </div>
        </div>
      </div>
      <div className="profile-rating-explanation">
        <h3>What your rating means</h3>
        {interpretation ? (
          <p className="profile-rating-interpretation">
            {provisional
              ? `Based on your sessions so far, ${interpretation[0]!.toLowerCase()}${interpretation.slice(1)}`
              : interpretation}
          </p>
        ) : (
          <p className="profile-rating-interpretation">
            {rating.status === 'unrated'
              ? 'Your rating will appear after your first completed session.'
              : 'Your rating is currently unavailable. Your session results are below.'}
          </p>
        )}
        <p>
          Each rated session compares your finish with the other players in that session. Your
          overall rating brings those results together over time.
        </p>
      </div>
      {rating.status === 'established' && rating.overallRank ? (
        <p className="profile-rating-rank">
          Overall rank · {rating.overallRank.rank} of {rating.overallRank.of}
        </p>
      ) : null}
      <details className="profile-rating-about">
        <summary>
          How ratings work <span aria-hidden="true">→</span>
        </summary>
        <div className="profile-rating-help">
          <h3>Understanding your {brand} rating</h3>
          <h4>Your finish matters.</h4>
          <p>
            Each session compares your final score with the other players in that session and gives
            you a percentile. Tied scores share the average of their positions.
          </p>
          <h4>Your results come together.</h4>
          <p>
            Your overall rating is the average of all your session percentiles. Each session counts
            equally. Higher ratings reflect stronger finishes.
          </p>
          <h4>It develops over time.</h4>
          <p>
            Your rating is provisional until you’ve completed{' '}
            {RATING_ESTABLISHED_AT === 4 ? 'four' : RATING_ESTABLISHED_AT} sessions. Early ratings
            can move more noticeably as new results come in.
          </p>
          <h4>It follows you across leagues.</h4>
          <p>Your rating reflects your {brand} sessions, rather than just your current league.</p>
        </div>
      </details>
    </section>
  );
}

export function RatingJourney({ rating }: { rating: PlayerRating }) {
  const history = rating.history.filter((h) => h.cumulativeRating !== undefined);
  const latest = rating.history.at(-1);
  const result = (h: PlayerRating['history'][number]) => (
    <>
      <h3>{h.leagueName ?? 'League session'}</h3>
      <p className="text-sm text-ink-2">
        {new Date(h.startsAt).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          timeZone: h.timeZone ?? 'UTC',
        })}
      </p>
      <dl className="profile-result-stats">
        <div>
          <dt>Session score</dt>
          <dd>{h.sessionScore ?? 'Unavailable'}</dd>
        </div>
        <div>
          <dt>Session percentile</dt>
          <dd>{h.percentile.toFixed(1)}</dd>
        </div>
      </dl>
      <details className="profile-session-detail">
        <summary className="profile-result-link">
          View results <span aria-hidden="true">→</span>
        </summary>
        {h.cumulativeRating !== undefined ? (
          <p className="text-sm">Rating after session: {h.cumulativeRating.toFixed(1)}</p>
        ) : null}
        {h.leagueId ? (
          <Link
            className="profile-result-link"
            to={`/my?leagueId=${encodeURIComponent(h.leagueId)}`}
          >
            League session details
          </Link>
        ) : (
          <p className="text-sm text-ink-2">League details unavailable.</p>
        )}
      </details>
    </>
  );
  return (
    <div className="profile-journey-grid">
      <section className="profile-card profile-journey">
        <h2>Your mahjong journey</h2>
        <p className="text-ink-2">Every session adds to your story.</p>
        {history.length ? (
          <>
            <PercentileTrend
              history={history.map((h) => ({ ...h, percentile: h.cumulativeRating! }))}
            />
            <p className="profile-history-values">
              {history.length === 1 ? (
                <>
                  <strong>{history[0]!.cumulativeRating!.toFixed(1)}</strong>
                  <br />
                  First session
                </>
              ) : (
                'Based on current finalized results.'
              )}
            </p>
          </>
        ) : (
          <p className="profile-empty">Your journey begins with your first completed session.</p>
        )}
      </section>
      <section className="profile-card profile-latest">
        <h2>Latest at the table</h2>
        {latest ? (
          <div className="profile-result">{result(latest)}</div>
        ) : (
          <p className="profile-empty">No completed sessions yet.</p>
        )}
        {rating.history.length > 1 ? (
          <details className="mt-3 text-sm">
            <summary className="min-h-[44px] cursor-pointer py-2">
              All session results ({rating.history.length})
            </summary>
            <ol className="space-y-3">
              {[...rating.history].reverse().map((h) => (
                <li className="profile-result" key={h.sessionId}>
                  {result(h)}
                </li>
              ))}
            </ol>
          </details>
        ) : null}
      </section>
    </div>
  );
}

function ProfilePace({
  value,
  disabled,
  onChange,
}: {
  value: PlayingPace | null;
  disabled: boolean;
  onChange: (pace: PlayingPace) => void;
}) {
  const id = useId();
  return (
    <fieldset className="profile-pace-options" disabled={disabled}>
      <legend className="sr-only">Preferred playing pace</legend>
      {PACE_OPTIONS.map((option) => (
        <label key={option.value}>
          <span className="font-display text-[23px]">{option.label}</span>
          <span className="text-sm text-ink-2">{option.description}</span>
          <input
            type="radio"
            name={id}
            value={option.value}
            checked={(value ?? 'unknown') === option.value}
            onChange={() => onChange(option.value)}
          />
        </label>
      ))}
    </fieldset>
  );
}

/**
 * CUMULATIVE RATING HISTORY AS A LINE — one series over time, so no legend: the heading above names it.
 *
 * Built to the charting rules: one hue from the tokens (`deep`), a 2px line, 8px markers, recessive
 * gridlines at 0, 50 and 100 with their labels OUTSIDE the plot on the left, so no label ever sits
 * on the line. Hovering or focusing a point shows its value ABOVE the plot, in clear space, rather
 * than as a floating label over the marks. The row of numbers below is the table view.
 */
function PercentileTrend({ history }: { history: PlayerRating['history'] }) {
  const [active, setActive] = useState<number | null>(null);

  const width = 320;
  const height = 96;
  const left = 34; // room for the 0/50/100 labels, outside the plot
  const right = 8;
  const top = 8;
  const bottom = 8;
  /* Points start and end this far inside the gridlines, so no marker sits against a label. */
  const inset = 14;
  const plotW = width - left - right;
  const plotH = height - top - bottom;

  const x = (i: number) =>
    left +
    inset +
    (history.length === 1
      ? (plotW - 2 * inset) / 2
      : (i * (plotW - 2 * inset)) / (history.length - 1));
  const y = (p: number) => top + ((100 - p) / 100) * plotH;
  const path = history
    .map((h, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(h.percentile).toFixed(1)}`)
    .join(' ');
  const shown = active === null ? null : history[active]!;

  return (
    <div className="mt-1">
      {/* The hover readout lives here, above the plot — never over the line. */}
      <p className="tabular h-5 text-[12px] text-ink-2" aria-live="polite">
        {shown
          ? `Session ${active! + 1} · ${new Date(shown.startsAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: shown.timeZone ?? 'UTC' })} · ${shown.percentile.toFixed(1)}`
          : ''}
      </p>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        role="img"
        aria-label={`Cumulative ratings, oldest to newest: ${history.map((h) => h.percentile.toFixed(1)).join(', ')}`}
        onPointerLeave={() => setActive(null)}
      >
        {[0, 50, 100].map((g) => (
          <g key={g}>
            <line
              x1={left}
              x2={width - right}
              y1={y(g)}
              y2={y(g)}
              className="stroke-light"
              strokeWidth={1}
            />
            <text
              x={left - 6}
              y={y(g)}
              textAnchor="end"
              dominantBaseline="middle"
              className="fill-ink-3 text-[11px]"
            >
              {g}
            </text>
          </g>
        ))}
        {history.length > 1 ? (
          <path
            d={path}
            fill="none"
            className="stroke-deep"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : null}
        {history.map((h, i) => (
          <g key={h.sessionId}>
            <circle
              cx={x(i)}
              cy={y(h.percentile)}
              r={4}
              className={active === i ? 'fill-signal' : 'fill-deep'}
            />
            {/*
              A hit target larger than the mark, for the POINTER only. Inside a `role="img"` every
              child is presentational, so a label or a tab stop here is an ARIA violation (axe:
              aria-prohibited-attr). Nothing is lost by it: the svg's own label reads every value,
              and the same numbers are printed as text beneath the chart.
            */}
            <rect
              x={x(i) - 12}
              y={top}
              width={24}
              height={plotH}
              fill="transparent"
              onPointerEnter={() => setActive(i)}
            />
          </g>
        ))}
      </svg>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <PlayerPage className="profile-dashboard">{children}</PlayerPage>;
}

export function Stat({
  label,
  value,
  icon,
}: {
  label: string;
  value: number | null;
  icon: React.ReactNode;
}) {
  return (
    <div className="profile-stat">
      <span aria-hidden="true">{icon}</span>
      <dl>
        <dt>{label}</dt>
        <dd className="tabular">
          {value === null ? (
            <>
              —<span className="sr-only"> Not recorded yet</span>
            </>
          ) : (
            value.toLocaleString('en-US')
          )}
        </dd>
      </dl>
    </div>
  );
}

/** A real checkbox behind a switch, so it is reachable by keyboard and announced as a switch. */
function Toggle({
  label,
  hint,
  on,
  onChange,
}: {
  label: string;
  hint?: string;
  on: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-light bg-paper px-4 py-4">
      <input
        type="checkbox"
        role="switch"
        checked={on}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 size-5 shrink-0 accent-[var(--color-deep)]"
      />
      <span className="min-w-0">
        <span className="block text-[14px] font-medium">{label}</span>
        {hint ? (
          <span className="mt-1 block text-[13px] leading-relaxed text-ink-2">{hint}</span>
        ) : null}
      </span>
    </label>
  );
}
